/**
 * hooks/useVisualViewport.ts
 *
 * Tracks the visual viewport to detect mobile keyboard open/close.
 * Uses the VisualViewport API (supported on all modern iOS/Android browsers).
 * Falls back gracefully when the API is unavailable.
 *
 * Returns:
 * - isKeyboardOpen: true when the virtual keyboard is likely visible
 * - viewportHeight: current visual viewport height in px
 *
 * Created June 2026: mobile-first form experience improvements.
 */

import { useState, useEffect } from 'react';

interface VisualViewportState {
  isKeyboardOpen: boolean;
  viewportHeight: number;
}

export function useVisualViewport(): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(() => ({
    isKeyboardOpen: false,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const baselineHeight = vv.height;
    // Track previous values in a ref to avoid setState on unchanged frames
    const prev = { isKeyboardOpen: false, viewportHeight: vv.height };

    const update = () => {
      const currentHeight = vv.height;
      const isKeyboardOpen = baselineHeight - currentHeight > 150;
      if (isKeyboardOpen === prev.isKeyboardOpen && currentHeight === prev.viewportHeight) return;
      prev.isKeyboardOpen = isKeyboardOpen;
      prev.viewportHeight = currentHeight;
      setState({ isKeyboardOpen, viewportHeight: currentHeight });
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return state;
}
